import { useEffect, useRef, useState } from "react";
import {
  BriefingLineSyncState,
  BriefingMetricsStatus,
  useGetBriefingMetricsStatusQuery,
} from "../store/apis/briefingApi";

/** Mientras alguna línea se esté actualizando, el estado se vuelve a pedir cada tanto. */
export const STATUS_POLL_MS = 5000;

/** Estados que significan que la línea no tiene los datos al día. */
export const PROBLEM_STATES: BriefingLineSyncState[] = [
  "ERROR",
  "NO_DATA",
  "NOT_CLOSED",
  "STALE",
  "NEVER_SYNCED",
];

export interface MetricsStatusCounts {
  inProgress: number;
  problems: number;
  /** Líneas enlazadas a una campaña de plataforma: las únicas que se pueden actualizar. */
  linkedLines: number;
}

export const countMetricsStatus = (
  summary?: BriefingMetricsStatus["summary"]
): MetricsStatusCounts => ({
  inProgress: summary?.IN_PROGRESS ?? 0,
  problems: PROBLEM_STATES.reduce(
    (sum, state) => sum + (summary?.[state] ?? 0),
    0
  ),
  linkedLines: (summary?.total ?? 0) - (summary?.NOT_LINKED ?? 0),
});

/**
 * Estado de sincronización del briefing a nivel de página.
 *
 * Vive en la página y no en la pestaña de "Datos de plataforma" porque la pestaña solo está
 * montada mientras se ve: si se pulsa "Actualizar datos" y se vuelve a los detalles, el sondeo
 * tiene que seguir y, al terminar, recargar los reales que se están mirando. La pestaña lee la
 * misma consulta, que RTK Query comparte, así que no se pide dos veces.
 *
 * @param onFinished se llama cuando pasan de haber líneas actualizándose a no haber ninguna.
 */
export const useBriefingMetricsSync = (
  briefingId: string | undefined,
  onFinished?: () => void,
  options: { skip?: boolean } = {}
) => {
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data } = useGetBriefingMetricsStatusQuery(
    { id: briefingId ?? "" },
    { skip: !briefingId || Boolean(options.skip), pollingInterval }
  );

  const status = data?.data;
  const counts = countMetricsStatus(status?.summary);
  const { inProgress } = counts;

  useEffect(() => {
    setPollingInterval(inProgress > 0 ? STATUS_POLL_MS : 0);
  }, [inProgress]);

  const previousInProgress = useRef(0);
  useEffect(() => {
    if (previousInProgress.current > 0 && inProgress === 0) {
      onFinished?.();
    }
    previousInProgress.current = inProgress;
  }, [inProgress, onFinished]);

  return { status, ...counts };
};
